// @flow
import React, { useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Confirmation, withConfirmation, connectAPI } from 'speed-js-react';
import { openPanel } from 'esm-web-pro/redux/side-panel';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import Editor from 'esm-web-pro/components/pages/projectManagement/dashboard/Editor';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import { Link } from 'react-router-dom';
import {
    injectIntl, IntlShape, FormattedMessage, defineMessages,
} from 'react-intl';
import BreakSpliceIcon from 'esm-web-pro/components/layout/sidePanel/icons/BreakSpliceIcon';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { Box } from 'grommet';
import ShowOnMap from 'esm-web-pro/assets/images/map/showOnMap.svg';
import { ListPageContext } from '../../ListPage';

const messages = defineMessages({
    unlinkObject: {
        id: 'unlink-gis-confirm',
        defaultMessage: 'Are you sure you want to unlink the object?',
    },
    actionFailed: {
        id: 'response-error',
        defaultMessage: 'Error occurred, please try again later.',
    },
});

type Props = {
    editorKey: String,
    resourceName: String,
    excludeFields: Array,
    permissions: Array,
    mapObject: Object,
    reload: Function,
    confirm: (confirmation: Confirmation) => void,
    resourceArguments: Array,
    intl: IntlShape,
    unlink: Function,
    getObjectType: Function,
}

const GisPointActions = (props: Props) => {
    const {
        editorKey, mapObject, reload, confirm, resourceArguments,
        intl, unlink, resourceName, excludeFields, permissions, getObjectType,
    } = props;

    const [uuids, setUuids] = useState([]);
    const [type, setType] = useState('');
    const [mapObjectUrl, setMapObjectUrl] = useState('');

    const formRef = React.useRef();

    const listPage = useContext(ListPageContext);

    const dispatch = useDispatch();

    useEffect(() => {
        setType(getObjectType(mapObject));
    }, [mapObject, getObjectType]);

    useEffect(() => {
        setUuids([...uuids, mapObject.uuid]);
    }, [mapObject]);

    // Get url for opening an object in new tab
    useEffect(() => {
        if (type) {
            let objectType;
            let identity;
            if (type === 'LineString') {
                objectType = 'gisline';
                identity = mapObject.gis_line_identity;
            } else {
                const { identity: objIdentity } = mapObject.gis_point.point;
                objectType = type;
                identity = objIdentity;
            }
            setMapObjectUrl(`/infrastructure/map/${mapObject.map}/active/layer/${objectType}/${identity}`);
        }
    }, [type]);

    const invokeConfirmDialog = ((message, confirmCallback) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {},
                type: 'negative',
            },
            confirm: {
                label: <FormattedMessage id="confirm-btn" defaultMessage="Confirm" />,
                callback: confirmCallback,
                type: 'positive',
            },
        },
    }));

    const unlinkObjectFromProjectCallback = () => {
        if (uuids.length > 0) {
            unlink({ type, uuids })
                .then(() => listPage.table.current.reloadData());
        }
    };

    // Link to a new tab to show an object on the map
    const linkAction = (url) => (
        <Link to={url} rel="noopener noreferrer" target="_blank">
            <img src={ShowOnMap} alt="show-on-map-icon" />
        </Link>
    );

    const mapObjectEditorTitle = (
        <FormattedMessage
            id="project-edit-object"
            defaultMessage="Edit object"
        />
    );

    const objectActions = [
        {
            key: `${mapObject.id}-edit`,
            icon: <EditIcon size="small" />,
            callback: () => dispatch(openPanel(editorKey, '', null, true)),
            tooltip: intl.formatMessage(actionMessages.edit),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
            permissions: permissions.change,
        },
        {
            key: `${mapObject.id}-unlink`,
            icon: <BreakSpliceIcon size="small" />,
            callback: () => invokeConfirmDialog(
                intl.formatMessage(messages.unlinkObject), unlinkObjectFromProjectCallback,
            ),
            tooltip: intl.formatMessage(actionMessages.split),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
            permissions: permissions.delete,
        },
        {
            key: `${mapObject.id}-showOnMap`,
            icon: linkAction(mapObjectUrl),
            tooltip: intl.formatMessage(actionMessages.view),
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        },
    ];

    const onSubmit = () => {
        const data = {
            project_stage: +formRef.current.state.values.project_stage,
        };
        reload();
        return data;
    };

    return (
        <>
            <Box>
                <Box>
                    <Editor
                        id={editorKey}
                        object={mapObject}
                        resourceName={resourceName}
                        resourceArguments={resourceArguments}
                        excludeFields={excludeFields}
                        permissions={permissions}
                        idAccessor="uuid"
                        editorTitle={mapObjectEditorTitle}
                        onSave={reload}
                        onSubmit={onSubmit}
                        forwardRef={formRef}
                    />
                    <MainActions
                        main
                        actions={objectActions}
                    />
                </Box>
            </Box>
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    unlink: {
        send: (data: array) => apiClient.resources.groupActions.unlink(data),
    },
});

export default connectAPI(apiCallback)(injectIntl(withConfirmation(GisPointActions)));
